import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import {
  Box,
  InputAdornment
} from "@mui/material";
import {
  Email as EmailIcon,
  Lock as LockIcon,
  AccountCircle as AccountCircleIcon,
  Work as WorkIcon
} from "@mui/icons-material";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import API_BASE_URL from "../../components/config/appConfig";
import { registerSchema } from "../../Validations/schema";
import { toLowerCaseEmail } from "../../utilities/email";
import { AuthBoardArt } from "./authArt";
import { IconBoard, IconUsers, IconShield } from "./landingArt";
import {
  AuthLayout,
  AuthNotice,
  AuthTextField,
  AuthPrimaryButton,
  AuthError,
  AuthRise
} from "./authUi";
import "./home.scss";

const STAGE_POINTS = [
  { icon: IconBoard, label: "Sprint boards" },
  { icon: IconUsers, label: "Your whole team" },
  { icon: IconShield, label: "Secure access" },
];

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  let urlQueryObj = Object.fromEntries([...searchParams]);

  const [success, setSuccess] = useState('');
  const [registrationFailure, SetRegistrationFailure] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSubmit = async (values) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/signUp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...values, email: toLowerCaseEmail(values.email) }),
      });
      const data = await response.json();

      if (!response.ok) {
        SetRegistrationFailure(data.message);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        throw new Error(data.message || "Registration failed");
      } else {
        setSuccess(data.message || "Registration successful!");
        setSnackbarOpen(true);
        setSnackbarSeverity("success");
        navigate("/login");
      }
    } catch (error) {
      console.error("Registration failed:", error.message);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      navigate("/projects");
    }
  }, []);

  const notice = (
    <AuthNotice
      open={snackbarOpen}
      onClose={handleSnackbarClose}
      severity={snackbarSeverity}
    >
      {snackbarSeverity === "success" ? success : registrationFailure}
    </AuthNotice>
  );

  return (
    <AuthLayout
      notice={notice}
      variant="au-card--tall"
      title={<>Get your team<br /><em>moving together.</em></>}
      lead="Create an account and start planning sprints, tracking work and shipping on time."
      points={STAGE_POINTS}
      art={<AuthBoardArt />}
    >
      <h2 className="au-title au-rise" style={{ '--au-d': '.06s' }}>
        Create your account
      </h2>
      <p className="au-sub au-rise" style={{ '--au-d': '.12s' }}>
        Register to get started with your account
      </p>

      <Formik
        initialValues={{
          name: "",
          email: urlQueryObj?.Email ? urlQueryObj?.Email : "",
          password: "",
          role: "",
          department: "",
          organization: urlQueryObj?.organization ? urlQueryObj?.organization : null
        }}
        validationSchema={registerSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched }) => (
          <Form>
            <AuthRise delay={0.18} mb={2.5}>
              <Field name="name">
                {({ field }) => (
                  <AuthTextField
                    {...field}
                    label="Full Name"
                    variant="outlined"
                    fullWidth
                    placeholder="Enter your full name"
                    error={touched.name && !!errors.name}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccountCircleIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </Field>
              <AuthError name="name" />
            </AuthRise>

            <AuthRise delay={0.24} mb={2.5}>
              <Field name="email">
                {({ field }) => (
                  <AuthTextField
                    {...field}
                    label="Email Address"
                    variant="outlined"
                    fullWidth
                    placeholder="Enter your email address"
                    disabled={!!urlQueryObj?.Email}
                    value={urlQueryObj?.Email || field.value}
                    error={touched.email && !!errors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </Field>
              <AuthError name="email" />
            </AuthRise>

            <AuthRise delay={0.3} mb={2.5}>
              <Field name="password">
                {({ field }) => (
                  <AuthTextField
                    {...field}
                    label="Password"
                    variant="outlined"
                    fullWidth
                    placeholder="Create a strong password"
                    type="password"
                    error={touched.password && !!errors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </Field>
              <AuthError name="password" />
            </AuthRise>

            <AuthRise delay={0.36} mb={3.5}>
              <Field name="department">
                {({ field }) => (
                  <AuthTextField
                    {...field}
                    label="Designation"
                    variant="outlined"
                    fullWidth
                    placeholder="Enter your designation"
                    error={touched.department && !!errors.department}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WorkIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </Field>
              <AuthError name="department" />
            </AuthRise>

            <AuthRise delay={0.42}>
              <AuthPrimaryButton
                type="submit"
                variant="contained"
                fullWidth
                disabled={isSubmitting}
                size="large"
                disableElevation
              >
                {isSubmitting ? "Creating account..." : "Create account"}
              </AuthPrimaryButton>
            </AuthRise>
          </Form>
        )}
      </Formik>

      <Box className="au-foot au-rise" style={{ '--au-d': '.48s' }}>
        Already have an account?
        <NavLink className="au-link" to="/login">Sign in</NavLink>
      </Box>
    </AuthLayout>
  );
};

export default Register;
